import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { AssessmentRole, User } from "../../../API";
import { useTranslation } from "react-i18next";
import {
  Dispatch,
  HTMLAttributes,
  MouseEventHandler,
  SetStateAction
} from "react";
import { OptionCard } from "../../../common-ui/OptionCard";
import { AvailableParticipant } from "../../Users/usersAPI";

type ParticipantCardProps = {
  user: AvailableParticipant;
  props?: HTMLAttributes<HTMLLIElement>;
  selected?: boolean;
  setParticipant: Dispatch<SetStateAction<AvailableParticipant | undefined>>;
  setParticipantQuery: Dispatch<SetStateAction<string>>;
  clearable?: boolean;
  disabled?: boolean;
  isMenuItem?: boolean;
  autoCompleteOnClick?: MouseEventHandler<HTMLLIElement> | undefined;
};

const ParticipantCard = ({
  user,
  props,
  selected,
  setParticipant,
  setParticipantQuery,
  clearable,
  disabled,
  isMenuItem,
  autoCompleteOnClick
}: ParticipantCardProps) => {
  const { fullName, initials, sharedProgramsDisplay } = user;

  const clearFunction = () => {
    setParticipant(undefined);
    setParticipantQuery("");
  };

  return (
    <OptionCard
      avatarContent={initials}
      passedProps={props}
      title={fullName}
      subtitle={sharedProgramsDisplay}
      selected={selected}
      setOption={setParticipant}
      clearFunction={clearFunction}
      clearable={clearable}
      disabled={disabled}
      isMenuItem={isMenuItem}
      autoCompleteOnClick={autoCompleteOnClick}
    />
  );
};

type ParticipantSelectProps = {
  availableParticipants: AvailableParticipant[];
  participant?: AvailableParticipant;
  setParticipant: Dispatch<SetStateAction<AvailableParticipant | undefined>>;
  setParticipantQuery: Dispatch<SetStateAction<string>>;
  participantAssessmentRole?: string;
  disabled?: boolean;
  loading?: boolean;
};

export const ParticipantSelect = ({
  availableParticipants,
  participant,
  setParticipant,
  disabled,
  setParticipantQuery,
  participantAssessmentRole,
  loading
}: ParticipantSelectProps) => {
  const { t } = useTranslation();

  if (participant) {
    return (
      <ParticipantCard
        user={participant as any}
        setParticipant={setParticipant}
        setParticipantQuery={setParticipantQuery}
        selected
        clearable
        disabled={disabled}
      />
    );
  }

  return (
    <Autocomplete
      options={availableParticipants}
      disabled={disabled}
      id="participant-select-input"
      getOptionLabel={(option) => `${option.fullName}`}
      renderOption={(props, option) => {
        return (
          <ParticipantCard
            key={option.userId}
            user={option}
            props={props}
            setParticipant={setParticipant}
            setParticipantQuery={setParticipantQuery}
            isMenuItem
            autoCompleteOnClick={props.onClick}
          />
        );
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            label={t(`Search ${participantAssessmentRole}`)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        );
      }}
      onInputChange={(event, value, reason) => {
        setParticipantQuery(value);
      }}
      onChange={(event, value, reason) => {
        if (reason === "selectOption" && value) {
          setParticipant(value);
        }
      }}
    />
  );
};
